import React from "react";

const ROW_SHIMMER_WIDTHS = ["92%", "78%", "88%", "70%", "85%", "74%"];

type CuadroMandoTableLoadingProps = {
  colSpan?: number;
};

/**
 * Sustituto visual del skeleton por fila/columna en el cuadro de mando.
 */
export function CuadroMandoTableLoading({
  colSpan = 18,
}: CuadroMandoTableLoadingProps) {
  const [pulse, setPulse] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setPulse((p) => (p + 1) % 3);
    }, 600);
    return () => window.clearInterval(id);
  }, []);

  const dots = ".".repeat((pulse % 3) + 1);

  return (
    <tr>
      <td colSpan={colSpan} className="p-0 border-0 bg-white">
        <div
          className="cuadro-mando-table-loading"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Cargando datos del cuadro de mando"
        >
          <div className="cuadro-mando-table-loading__center">
            <div className="cuadro-mando-table-loading__spinner-wrap">
              <span className="cuadro-mando-table-loading__ring" aria-hidden />
              <span className="cuadro-mando-table-loading__ring cuadro-mando-table-loading__ring--delay" aria-hidden />
            </div>
            <div className="cuadro-mando-table-loading__text">
              <span className="cuadro-mando-table-loading__title">
                Preparando cuadro de mando{dots}
              </span>
              <span className="cuadro-mando-table-loading__subtitle">
                Obteniendo colaboradores, horas y pagos del periodo
              </span>
            </div>
          </div>

          <div className="cuadro-mando-table-loading__rows" aria-hidden>
            {ROW_SHIMMER_WIDTHS.map((width, i) => (
              <div
                key={i}
                className="cuadro-mando-table-loading__row"
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <span className="cuadro-mando-table-loading__avatar" />
                <span
                  className="cuadro-mando-table-loading__bar"
                  style={{ width }}
                />
              </div>
            ))}
          </div>
        </div>

        <style>{`
          .cuadro-mando-table-loading {
            min-height: min(52vh, 520px);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 2rem;
            padding: 2.5rem 1.5rem 2rem;
            background: linear-gradient(
              180deg,
              #ffffff 0%,
              #f8fafc 55%,
              #f1f5f9 100%
            );
          }

          .cuadro-mando-table-loading__center {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1.15rem;
            text-align: center;
          }

          .cuadro-mando-table-loading__spinner-wrap {
            position: relative;
            width: 3.25rem;
            height: 3.25rem;
          }

          .cuadro-mando-table-loading__ring {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: 3px solid #e0e7ff;
            border-top-color: #2563eb;
            animation: cuadroTableSpin 0.9s linear infinite;
          }

          .cuadro-mando-table-loading__ring--delay {
            inset: 0.35rem;
            border-width: 2px;
            border-color: #dbeafe;
            border-bottom-color: #60a5fa;
            animation-duration: 1.4s;
            animation-direction: reverse;
          }

          .cuadro-mando-table-loading__title {
            display: block;
            font-size: 1.05rem;
            font-weight: 700;
            color: #1e3a8a;
            letter-spacing: -0.01em;
          }

          .cuadro-mando-table-loading__subtitle {
            display: block;
            margin-top: 0.35rem;
            font-size: 0.85rem;
            font-weight: 500;
            color: #64748b;
            max-width: 22rem;
            line-height: 1.45;
          }

          .cuadro-mando-table-loading__rows {
            width: 100%;
            max-width: 36rem;
            display: flex;
            flex-direction: column;
            gap: 0.65rem;
          }

          .cuadro-mando-table-loading__row {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            opacity: 0.85;
            animation: cuadroTableRowFade 1.8s ease-in-out infinite;
          }

          .cuadro-mando-table-loading__avatar {
            flex-shrink: 0;
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            background: linear-gradient(
              90deg,
              #e2e8f0 0%,
              #f1f5f9 45%,
              #e2e8f0 90%
            );
            background-size: 200% 100%;
            animation: cuadroTableShimmer 1.4s ease-in-out infinite;
          }

          .cuadro-mando-table-loading__bar {
            height: 0.65rem;
            border-radius: 999px;
            background: linear-gradient(
              90deg,
              #e2e8f0 0%,
              #f8fafc 42%,
              #cbd5e1 50%,
              #f8fafc 58%,
              #e2e8f0 100%
            );
            background-size: 200% 100%;
            animation: cuadroTableShimmer 1.4s ease-in-out infinite;
          }

          @keyframes cuadroTableSpin {
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes cuadroTableShimmer {
            0% {
              background-position: 100% 0;
            }
            100% {
              background-position: -100% 0;
            }
          }

          @keyframes cuadroTableRowFade {
            0%,
            100% {
              opacity: 0.55;
            }
            50% {
              opacity: 1;
            }
          }
        `}</style>
      </td>
    </tr>
  );
}
